(() => {
  function initCollectorKnivesHeader() {
    const headers = document.querySelectorAll('[data-ck-header]');

    headers.forEach((header) => {
      const section = header.closest('.shopify-section');

      if (!section || section.dataset.ckHeaderReady === 'true') return;

      section.dataset.ckHeaderReady = 'true';

      /*
      |--------------------------------------------------------------------------
      | MOBILE DRAWER
      |--------------------------------------------------------------------------
      */

      const drawer = section.querySelector('[data-ck-drawer]');
      const openButton = section.querySelector('[data-ck-drawer-open]');
      const closeButtons = section.querySelectorAll('[data-ck-drawer-close]');

      const setDrawer = (open) => {
        if (!drawer) return;

        drawer.classList.toggle('is-open', open);
        drawer.setAttribute('aria-hidden', String(!open));

        if (openButton) {
          openButton.setAttribute('aria-expanded', String(open));
        }

        document.documentElement.classList.toggle('ck-drawer-open', open);
        document.body.classList.toggle('ck-drawer-open', open);
      };

      if (openButton) {
        openButton.addEventListener('click', () => setDrawer(true));
      }

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => setDrawer(false));
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          setDrawer(false);
        }
      });

      /*
      |--------------------------------------------------------------------------
      | CART SYNC
      |--------------------------------------------------------------------------
      */

      const cartLinks = section.querySelectorAll('[data-ck-cart-link]');
      const cartCounts = section.querySelectorAll('[data-ck-cart-count]');
      const cartTotals = section.querySelectorAll('[data-ck-cart-total]');

      const defaultCurrency =
        header.dataset.currency || 'USD';

      const cartEndpoint =
        header.dataset.cartEndpoint || '/cart.js';

      const formatCartTotal = (amount, currencyCode) => {
        const numericAmount = Number(amount);

        if (!Number.isFinite(numericAmount)) {
          return null;
        }

        try {
          return new Intl.NumberFormat(
            document.documentElement.lang || 'en-US',
            {
              style: 'currency',
              currency: currencyCode || defaultCurrency
            }
          ).format(numericAmount);
        } catch (error) {
          return `${currencyCode ? currencyCode + ' ' : ''}${numericAmount.toFixed(2)}`;
        }
      };

      const updateCustomCart = ({
        itemCount,
        totalAmount,
        currencyCode
      }) => {
        const parsedCount = Number(itemCount);

        const safeCount = Number.isFinite(parsedCount)
          ? Math.max(0, parsedCount)
          : 0;

        const itemWord =
          safeCount === 1
            ? 'item'
            : 'items';

        cartCounts.forEach((count) => {
          count.textContent = safeCount;
        });

        cartLinks.forEach((link) => {
          link.setAttribute(
            'aria-label',
            `Cart, ${safeCount} ${itemWord}`
          );
        });

        const formattedTotal = formatCartTotal(
          totalAmount,
          currencyCode
        );

        if (formattedTotal !== null) {
          cartTotals.forEach((total) => {
            total.textContent = formattedTotal;
          });
        }
      };

      const refreshCustomCart = async () => {
        try {
          const response = await fetch(cartEndpoint, {
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json'
            }
          });

          if (!response.ok) return;

          const currentCart = await response.json();

          updateCustomCart({
            itemCount: currentCart.item_count,
            totalAmount: Number(currentCart.total_price) / 100,
            currencyCode:
              currentCart.currency || defaultCurrency
          });
        } catch (error) {
          // Keep current values if request fails.
        }
      };

      const handleHorizonCartUpdate = (event) => {
        if (
          !event.promise ||
          typeof event.promise.then !== 'function'
        ) {
          refreshCustomCart();
          return;
        }

        event.promise
          .then(({ cart: updatedCart }) => {
            if (!updatedCart) {
              refreshCustomCart();
              return;
            }

            const totalMoney =
              updatedCart.cost?.totalAmount;

            updateCustomCart({
              itemCount: updatedCart.totalQuantity,
              totalAmount: totalMoney?.amount,
              currencyCode:
                totalMoney?.currencyCode ||
                defaultCurrency
            });
          })
          .catch(refreshCustomCart);
      };

      const handleLegacyCartUpdate = (event) => {
        const updatedCart =
          event.detail &&
          (
            event.detail.resource ||
            event.detail.cart
          );

        if (
          updatedCart &&
          typeof updatedCart.item_count !== 'undefined'
        ) {
          updateCustomCart({
            itemCount: updatedCart.item_count,
            totalAmount:
              typeof updatedCart.total_price !== 'undefined'
                ? Number(updatedCart.total_price) / 100
                : null,
            currencyCode:
              updatedCart.currency ||
              defaultCurrency
          });

          return;
        }

        refreshCustomCart();
      };

      document.addEventListener(
        'shopify:cart:lines-update',
        handleHorizonCartUpdate
      );

      document.addEventListener(
        'cart:update',
        handleLegacyCartUpdate
      );

      window.addEventListener(
        'pageshow',
        (event) => {
          if (event.persisted) {
            refreshCustomCart();
          }
        }
      );

      /*
      |--------------------------------------------------------------------------
      | PREDICTIVE SEARCH
      |--------------------------------------------------------------------------
      */

      const searchWrapper =
        section.querySelector(
          '[data-ck-predictive-search]'
        );

      const searchInput =
        searchWrapper?.querySelector(
          '[data-ck-search-input]'
        );

      const resultsBox =
        searchWrapper?.querySelector(
          '[data-ck-search-results]'
        );

      if (!searchInput || !resultsBox) {
        return;
      }

      const predictiveURL =
        header.dataset.predictiveUrl;

      const searchURL =
        header.dataset.searchUrl || '/search';

      let timer;
      let controller;

      const escapeHTML = (value = '') => {
        const element =
          document.createElement('div');

        element.textContent = value;

        return element.innerHTML;
      };

      const closeResults = () => {
        resultsBox.hidden = true;

        searchInput.setAttribute(
          'aria-expanded',
          'false'
        );
      };

      const renderResults = (
        products,
        query
      ) => {
        if (!products.length) {
          resultsBox.innerHTML =
            `<p class="ck-search-results__empty">
              No products found for “${escapeHTML(query)}”
            </p>`;
        } else {
          const cards = products
            .map((product) => {
              const image = product.image
                ? `
                  <img
                    src="${escapeHTML(product.image)}"
                    alt="${escapeHTML(product.title)}"
                    width="128"
                    height="128"
                    loading="lazy"
                  >
                `
                : '';

              const price = product.price
                ? `
                  <span class="ck-search-result__price">
                    ${escapeHTML(String(product.price))}
                  </span>
                `
                : '';

              return `
                <a
                  href="${escapeHTML(product.url)}"
                  class="ck-search-result"
                >
                  <span class="ck-search-result__image">
                    ${image}
                  </span>

                  <span>
                    <span class="ck-search-result__title">
                      ${escapeHTML(product.title)}
                    </span>

                    ${price}
                  </span>
                </a>
              `;
            })
            .join('');

          const allURL =
            `${searchURL}?q=${encodeURIComponent(query)}&type=product`;

          resultsBox.innerHTML = `
            <div class="ck-search-results__heading">
              Products
            </div>

            ${cards}

            <a
              href="${allURL}"
              class="ck-search-results__all"
            >
              View all results
            </a>
          `;
        }

        resultsBox.hidden = false;

        searchInput.setAttribute(
          'aria-expanded',
          'true'
        );
      };

      const fetchResults = async (query) => {
        if (!predictiveURL) return;

        if (controller) {
          controller.abort();
        }

        controller =
          new AbortController();

        const params =
          new URLSearchParams({
            q: query,
            'resources[type]': 'product',
            'resources[limit]': '6',
            'resources[options][unavailable_products]':
              'last'
          });

        try {
          const response = await fetch(
            `${predictiveURL}.json?${params.toString()}`,
            {
              signal: controller.signal,
              headers: {
                Accept: 'application/json'
              }
            }
          );

          if (!response.ok) {
            throw new Error(
              `Search request failed: ${response.status}`
            );
          }

          const data =
            await response.json();

          renderResults(
            data.resources?.results?.products || [],
            query
          );
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(
              '[CollectorKnives predictive search]',
              error
            );

            closeResults();
          }
        }
      };

      searchInput.addEventListener(
        'input',
        () => {
          const query =
            searchInput.value.trim();

          clearTimeout(timer);

          if (query.length < 2) {
            if (controller) {
              controller.abort();
            }

            closeResults();

            return;
          }

          timer = setTimeout(
            () => fetchResults(query),
            250
          );
        }
      );

      searchInput.addEventListener(
        'focus',
        () => {
          if (
            searchInput.value.trim().length >= 2 &&
            resultsBox.innerHTML.trim()
          ) {
            resultsBox.hidden = false;

            searchInput.setAttribute(
              'aria-expanded',
              'true'
            );
          }
        }
      );

      document.addEventListener(
        'click',
        (event) => {
          if (
            searchWrapper &&
            !searchWrapper.contains(event.target)
          ) {
            closeResults();
          }
        }
      );
    });
  }

  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initCollectorKnivesHeader
    );
  } else {
    initCollectorKnivesHeader();
  }

  /*
  |--------------------------------------------------------------------------
  | SHOPIFY THEME EDITOR
  |--------------------------------------------------------------------------
  */

  document.addEventListener(
    'shopify:section:load',
    initCollectorKnivesHeader
  );
})();